// Submit-boundary converter: a flat Formik price draft → the nested wire/storage
// shape (recurring XOR paymentPlan). The Formik tree stays flat by design
// (spec §7); this is one of the three boundaries (seed / submit / useProduct
// choke point) that keep everything OUTSIDE the tree nested.
//
// Contract:
// - Rebuilds the config EXCLUSIVELY from the flat draft fields and DISCARDS any
//   stale nested keys the seed spread carried onto the draft (getFormValues
//   spreads ...p, so a seeded draft holds the saved recurring/paymentPlan
//   objects alongside the flattened fields — the user's edits live in the flat
//   fields and must win).
// - Emits EXPLICIT nulls for the inactive config: under Mongoose subdoc merge
//   semantics omission is not removal, so a paymentPlan→subscription switch
//   must actively null `paymentPlan` (and one_time must null both).
// - Idempotent: an already-nested price (no flat billing fields) passes through
//   unchanged apart from re-asserting the explicit-null XOR — so the useProduct
//   choke point can run it on every payload.
// - Plan-ness derives from planIterations > 0, matching the legacy flat-wire
//   semantics (PriceForm guarantees planIterations >= 1 while the payment-plan
//   radio is selected, and clears it for subscriptions).

const toNumberOrNull = value => {
  if (value === '' || value == null) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

function cleanAnchorConfig(config) {
  if (config == null) return null;
  return {
    enabled: !!config.enabled,
    dayOfMonth: toNumberOrNull(config.dayOfMonth) ?? 1,
    month: toNumberOrNull(config.month),
  };
}

export default function draftToNestedPrice(draft) {
  if (!draft || typeof draft !== 'object') return draft;

  const {
    // UI-only RadioCard selections + tick-to-reveal flags — never persisted.
    __pricing,
    __schedule,
    __trial,
    __discountOpen,
    __startDateOpen,
    __setupFeeOpen,
    // Stale nested keys from the seed spread — discarded unless the draft has
    // no flat fields at all (i.e. it is already a nested payload).
    recurring: seededRecurring,
    paymentPlan: seededPaymentPlan,
    // The flat draft billing fields the config is rebuilt from.
    interval,
    intervalCount,
    trialPeriodDays,
    planIterations,
    billingCycleAnchorConfig,
    startDate,
    ...rest
  } = draft;

  const hasFlatFields = [interval, intervalCount, trialPeriodDays, planIterations, startDate].some(
    v => v != null && v !== '',
  );

  if (rest.billingType !== 'recurring') {
    return { ...rest, recurring: null, paymentPlan: null };
  }

  // Already-nested recurring payload (nothing flat to rebuild from): pass the
  // nested config through, re-asserting the explicit-null XOR.
  if (!hasFlatFields && (seededRecurring || seededPaymentPlan)) {
    return {
      ...rest,
      recurring: seededRecurring ?? null,
      paymentPlan: seededPaymentPlan ?? null,
    };
  }

  const config = {
    interval: interval || 'month',
    intervalCount: toNumberOrNull(intervalCount) ?? 1,
    trialPeriodDays: toNumberOrNull(trialPeriodDays),
    billingCycleAnchorConfig: cleanAnchorConfig(billingCycleAnchorConfig),
    startDate: startDate || null,
  };

  const iterations = toNumberOrNull(planIterations);
  if (iterations > 0) {
    return { ...rest, recurring: null, paymentPlan: { ...config, planIterations: iterations } };
  }
  return { ...rest, recurring: config, paymentPlan: null };
}
